import { useCallback } from 'react';

import type { AxePurchaseState } from '@deque/billing-service-client';
import {
  getControllingProductSubscription,
  listEnterpriseProductAccess,
  ProductSlugs
} from '@deque/billing-utils';
import { ACTIVE_STATES, INTEGRATION_PRODUCT_SLUGS } from '~/common/constants';
import { useEnterprises } from '~/common/contexts/enterprises';
import useAPI, { type State } from '~/common/hooks/useAPI';
import {
  type IntegrationConnection,
  getIntegrationConnections
} from '~/common/utils/integrations-client/jira/connections';

const useIntegrationConnections = (
  enterpriseId?: string,
  token?: string,
  skipRequest = false
): State<IntegrationConnection[]> => {
  const { activeEnterprise } = useEnterprises();
  const integrationProductSlugs = [
    ...INTEGRATION_PRODUCT_SLUGS,
    ProductSlugs.axeAuditorIntegration
  ];
  const productAccess = activeEnterprise
    ? listEnterpriseProductAccess(activeEnterprise)
    : null;
  const hasIntegrationSubscription = integrationProductSlugs.some(slug => {
    const controllingSubscription = activeEnterprise
      ? getControllingProductSubscription({
          entity: activeEnterprise,
          productSlug: slug as ProductSlugs
        })
      : null;

    return (
      ACTIVE_STATES.includes(productAccess?.[slug] as AxePurchaseState) &&
      ACTIVE_STATES.includes(
        controllingSubscription?.purchase_state as AxePurchaseState
      )
    );
  });

  const dataLoader = useCallback(() => {
    if (!enterpriseId) {
      return Promise.resolve([]);
    }

    if (!token) {
      return Promise.resolve([]);
    }

    if (!hasIntegrationSubscription) {
      return Promise.resolve([]);
    }

    return getIntegrationConnections({
      enterpriseId,
      token
    });
  }, [enterpriseId, token, hasIntegrationSubscription]);

  const { loading, error, data } = useAPI(dataLoader, skipRequest);

  return { loading, error, data };
};

export default useIntegrationConnections;
